import React, { useCallback } from 'react';

import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl, useIntl } from 'react-intl-next';

import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	type BlockMenuEventPayload,
} from '@atlaskit/editor-common/analytics';
import { BLOCK_MENU_ACTION_TEST_ID } from '@atlaskit/editor-common/block-menu';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { copyLinkToBlock, formatShortcut } from '@atlaskit/editor-common/keymaps';
import { blockMenuMessages as messages } from '@atlaskit/editor-common/messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { ToolbarDropdownItem, ToolbarKeyboardShortcutHint } from '@atlaskit/editor-toolbar';
import LinkIcon from '@atlaskit/icon/core/link';
import { fg } from '@atlaskit/platform-feature-flags';

import type { BlockMenuPlugin, BlockMenuPluginOptions } from '../blockMenuPluginType';
import { FLAG_ID } from '../blockMenuPluginType';
import { blockMenuPluginKey } from '../pm-plugins/main';

import { useBlockMenu } from './block-menu-provider';
import { BLOCK_MENU_ITEM_NAME } from './consts';
import { copyLink } from './utils/copyLink';

type Props = {
	api: ExtractInjectionAPI<BlockMenuPlugin> | undefined;
	config: BlockMenuPluginOptions | undefined;
};

const CopyLinkDropdownItemContent = ({ api, config }: Props & WrappedComponentProps) => {
	const { formatMessage } = useIntl();
	const { onDropdownOpenChanged } = useBlockMenu();
	const { getLinkPath, blockLinkHashPrefix } = config || {};

	const shortcut = formatShortcut(copyLinkToBlock);

	const { preservedSelection, defaultSelection } = useSharedPluginStateWithSelector(
		api,
		['blockControls', 'selection'],
		({ blockControlsState, selectionState }) => {
			return {
				preservedSelection: blockControlsState?.preservedSelection,
				defaultSelection: selectionState?.selection,
			};
		},
	);
	const selection = preservedSelection || defaultSelection;

	const handleClick = useCallback(() => {
		if (!selection) {
			return;
		}

		api?.core.actions.execute(({ tr }) => {
			const payload: BlockMenuEventPayload = {
				action: ACTION.CLICKED,
				actionSubject: ACTION_SUBJECT.BLOCK_MENU_ITEM,
				attributes: {
					menuItemName: BLOCK_MENU_ITEM_NAME.COPY_LINK_TO_BLOCK,
				},
				eventType: EVENT_TYPE.UI,
			};
			api?.analytics?.actions?.attachAnalyticsEvent(payload)(tr);

			api?.blockControls?.commands?.toggleBlockMenu({ closeMenu: true })({ tr });
			return tr;
		});

		onDropdownOpenChanged(false);

		copyLink({ getLinkPath, blockLinkHashPrefix, selection }).then((success) => {
			if (success) {
				api?.core.actions.execute(({ tr }) => {
					tr.setMeta(blockMenuPluginKey, {
						showFlag: FLAG_ID.LINK_COPIED_TO_CLIPBOARD,
					});
					return tr;
				});
			}
		});
	}, [api, blockLinkHashPrefix, getLinkPath, onDropdownOpenChanged, selection]);

	// Hide copy link when `platform_editor_adf_with_localid` feature flag is off
	if (!fg('platform_editor_adf_with_localid')) {
		return null;
	}

	// [FEATURE FLAG: platform_editor_block_menu_v2_patch_3]
	// Conditionally display message based on feature flag
	// To clean up: remove the conditional and use only copyLinkToSelection message
	const message = fg('platform_editor_block_menu_v2_patch_3')
		? messages.copyLinkToSelection
		: messages.copyLinkToBlock;

	// [FEATURE FLAG: platform_editor_block_menu_v2_patch_3]
	// Adds size="small" to icons for better visual consistency in block menu.
	// To clean up: remove conditional, keep only size="small" version.
	const iconSize = fg('platform_editor_block_menu_v2_patch_3') ? 'small' : undefined;

	return (
		<ToolbarDropdownItem
			onClick={handleClick}
			elemBefore={<LinkIcon label="" size={iconSize} />}
			elemAfter={shortcut ? <ToolbarKeyboardShortcutHint shortcut={shortcut} /> : undefined}
			ariaKeyshortcuts={shortcut}
			testId={BLOCK_MENU_ACTION_TEST_ID.COPY_LINK}
		>
			{formatMessage(message)}
		</ToolbarDropdownItem>
	);
};

export const CopyLinkDropdownItem = injectIntl(CopyLinkDropdownItemContent);
